/**
 * GP-1387: "Who verified what, and when?" — derived once, rendered everywhere.
 *
 * Verification/signature evidence for an order was scattered across four
 * unrelated shapes and never surfaced as a single per-step audit:
 *   - `metadata.clinicalVerification` {verifiedBy, verifiedAt, drugVerifications}
 *     written by ClinicalVerificationDialog (and mirrored into the
 *     `clinical_verified_by` / `clinical_verified_at` columns by
 *     OrdersService.update).
 *   - `metadata.productVerification` {verifiedBy, verifiedAt, drugVerifications}
 *     written by ProductVerificationDialog (mirrored into
 *     `product_verified_by` / `product_verified_at`).
 *   - `metadata.packagingData[]` + the `packaged_by` / `packaged_at` columns
 *     written by PackagingController.
 *   - Per-Rx hardcopy signatures from `GET /api/rx/:pharmacyId/signatures-batch`
 *     (data / clinical / product / dispensing).
 *
 * The Verification tab only ever looked at `metadata.clinicalVerification` and
 * bailed to "No verification data yet" when it was absent, and the order detail
 * view had no verification block at all. This module is the ONE place that maps
 * those writers onto an ordered list of fulfillment steps, so the tab, the order
 * detail page and anything added later cannot drift apart.
 *
 * Honesty rules (project rule: no silent fallbacks — never fabricate a performer
 * or a timestamp):
 *  - A step is returned ONLY when the order actually carries a record for it —
 *    a timestamp, a performer, or at least one captured signature. Steps with
 *    nothing recorded are omitted, so an empty array genuinely means "nothing
 *    has been verified yet".
 *  - `performedBy` / `performedAt` are left `undefined` when no writer stored
 *    them. Consumers render "Not recorded" — never today's date, never the
 *    current user.
 *  - Data Verification and Dispensing have no order-level columns: their stamp
 *    comes from the captured signature itself (the actual record), never from a
 *    neighbouring step's timestamp.
 */

export type VerificationStepKey =
  | 'data_verification'
  | 'clinical_verification'
  | 'packaging'
  | 'product_verification'
  | 'dispensing';

/** Signature row as returned by `/api/rx/:pharmacyId/signatures-batch`. */
export interface VerificationSignature {
  rxNumber?: string | number;
  signatureType?: string;
  initials?: string;
  userName?: string;
  userEmail?: string;
  signedAt?: string | Date;
}

export interface OrderVerificationStep {
  key: VerificationStepKey;
  label: string;
  /** Who completed the step. `undefined` = no writer recorded a performer. */
  performedBy?: string;
  /** ISO timestamp of completion. `undefined` = no writer recorded a time. */
  performedAt?: string;
  /** Factual one-liner (e.g. "2 drugs verified"). `undefined` = nothing to add. */
  detail?: string;
  /** Captured signatures for this step, one per Rx, newest first. */
  signatures: VerificationSignature[];
}

/**
 * Shape this module reads. Deliberately loose (all optional) — it is fed by the
 * order-detail response, the Patient-360 dialog and the queue payloads, which
 * carry the same fields under the same names but are typed separately.
 */
export interface VerifiableOrder {
  clinicalVerifiedBy?: string | null;
  clinicalVerifiedAt?: string | Date | null;
  packagedBy?: string | null;
  packagedAt?: string | Date | null;
  productVerifiedBy?: string | null;
  productVerifiedAt?: string | Date | null;
  metadata?: {
    clinicalVerification?: {
      verifiedBy?: string;
      verifiedAt?: string;
      drugVerifications?: unknown[];
    } | null;
    productVerification?: {
      verifiedBy?: string;
      verifiedAt?: string;
      drugVerifications?: unknown[];
    } | null;
    packagingData?:
      | Array<{
          packagedBy?: string;
          packagedAt?: string;
          scans?: Array<{ timestamp?: string }>;
        }>
      | { packagedBy?: string; packagedAt?: string }
      | null;
  } | null;
}

export const VERIFICATION_STEP_LABELS: Record<VerificationStepKey, string> = {
  data_verification: 'Data Verification',
  clinical_verification: 'Clinical Verification',
  packaging: 'Packaging',
  product_verification: 'Product Verification',
  dispensing: 'Dispensing',
};

/** Fulfillment order — the sequence a pharmacist expects to read top to bottom. */
const STEP_ORDER: VerificationStepKey[] = [
  'data_verification',
  'clinical_verification',
  'packaging',
  'product_verification',
  'dispensing',
];

/** Normalize a stored timestamp to an ISO string, or undefined when unusable. */
const toIso = (value: string | Date | null | undefined): string | undefined => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const trimmed = (value: string | null | undefined): string | undefined => {
  const text = typeof value === 'string' ? value.trim() : '';
  return text ? text : undefined;
};

const countLabel = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? '' : 's'} verified`;

/** Newest first, entries without a parseable timestamp last. */
const bySignedAtDesc = (a: VerificationSignature, b: VerificationSignature): number => {
  const left = toIso(a.signedAt);
  const right = toIso(b.signedAt);
  if (left && right) return right.localeCompare(left);
  if (left) return -1;
  if (right) return 1;
  return 0;
};

/**
 * Packaging stamp. `metadata.packagingData` ships in two shapes: the current
 * per-drug array written by PackagingController, and a legacy single object.
 * The `packaged_by`/`packaged_at` columns are the authoritative record for the
 * array shape; the per-item fields (or the newest box scan) only fill in for
 * older rows written before the columns were set.
 */
const resolvePackaging = (
  order: VerifiableOrder,
): { by?: string; at?: string; drugCount: number } => {
  const packagingData = order.metadata?.packagingData;
  const legacyObject =
    packagingData && !Array.isArray(packagingData) ? packagingData : undefined;
  const items = Array.isArray(packagingData) ? packagingData : [];

  const itemStamps = items
    .map((item) => ({
      by: trimmed(item?.packagedBy),
      at:
        toIso(item?.packagedAt) ??
        (item?.scans || [])
          .map((scan) => toIso(scan?.timestamp))
          .filter((iso): iso is string => !!iso)
          .sort()
          .pop(),
    }))
    .filter((stamp) => stamp.by || stamp.at)
    .sort((a, b) => (b.at || '').localeCompare(a.at || ''));

  return {
    by: trimmed(legacyObject?.packagedBy) ?? trimmed(order.packagedBy) ?? itemStamps[0]?.by,
    at: toIso(legacyObject?.packagedAt) ?? toIso(order.packagedAt) ?? itemStamps[0]?.at,
    drugCount: items.length,
  };
};

/**
 * Build the per-step verification audit for an order.
 *
 * @param order      Order carrying the verification metadata/columns.
 * @param signatures Hardcopy signatures for the order's Rx numbers (from
 *                   `signatures-batch`). Pass `[]` when they have not loaded —
 *                   steps still resolve from the order's own record.
 */
export function deriveOrderVerificationSteps(
  order: VerifiableOrder | null | undefined,
  signatures: VerificationSignature[] = [],
): OrderVerificationStep[] {
  if (!order) return [];

  const signaturesByStep = new Map<VerificationStepKey, VerificationSignature[]>();
  for (const signature of signatures || []) {
    const key = signature?.signatureType as VerificationStepKey | undefined;
    if (!key || !STEP_ORDER.includes(key)) continue;
    const list = signaturesByStep.get(key) || [];
    list.push(signature);
    signaturesByStep.set(key, list);
  }

  const clinicalMeta = order.metadata?.clinicalVerification;
  const productMeta = order.metadata?.productVerification;
  const packaging = resolvePackaging(order);

  const clinicalDrugs = Array.isArray(clinicalMeta?.drugVerifications)
    ? clinicalMeta!.drugVerifications!.length
    : 0;
  const productDrugs = Array.isArray(productMeta?.drugVerifications)
    ? productMeta!.drugVerifications!.length
    : 0;

  const stamps: Record<
    VerificationStepKey,
    { by?: string; at?: string; detail?: string }
  > = {
    // No order-level columns exist for these two — the signature IS the record.
    data_verification: {},
    clinical_verification: {
      by: trimmed(clinicalMeta?.verifiedBy) ?? trimmed(order.clinicalVerifiedBy),
      at: toIso(clinicalMeta?.verifiedAt) ?? toIso(order.clinicalVerifiedAt),
      detail: clinicalDrugs > 0 ? countLabel(clinicalDrugs, 'drug') : undefined,
    },
    packaging: {
      by: packaging.by,
      at: packaging.at,
      detail:
        packaging.drugCount > 0
          ? `${packaging.drugCount} drug${packaging.drugCount === 1 ? '' : 's'} packaged`
          : undefined,
    },
    product_verification: {
      by: trimmed(productMeta?.verifiedBy) ?? trimmed(order.productVerifiedBy),
      at: toIso(productMeta?.verifiedAt) ?? toIso(order.productVerifiedAt),
      detail: productDrugs > 0 ? countLabel(productDrugs, 'drug') : undefined,
    },
    dispensing: {},
  };

  const steps: OrderVerificationStep[] = [];
  for (const key of STEP_ORDER) {
    const stepSignatures = (signaturesByStep.get(key) || []).sort(bySignedAtDesc);
    const stamp = stamps[key];

    // The most recent signature stands in when the step has no column/metadata
    // record of its own (Data Verification, Dispensing, and pre-column orders).
    const latestSignature = stepSignatures[0];
    const performedBy = stamp.by ?? trimmed(latestSignature?.userName);
    const performedAt = stamp.at ?? toIso(latestSignature?.signedAt);

    if (!performedBy && !performedAt && stepSignatures.length === 0) continue;

    steps.push({
      key,
      label: VERIFICATION_STEP_LABELS[key],
      performedBy,
      performedAt,
      detail: stamp.detail,
      signatures: stepSignatures,
    });
  }

  return steps;
}
